import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import chatApi from "@/services/modules/chat";
import { MessageModel, RecipientModel } from "@/types/chat";
import accountApi from "@/features/admin/api/account";
import { FriendSuggestionModel } from "@/features/user";

const slice = createSlice({
  name: "chat",
  initialState: {
    messages: [],
    recipients: [],
    friendSuggestionList: [],
    friendRequestList: [],
  } as ChatState,
  reducers: {
    selectRecipient(
      state,
      { payload }: { payload: RecipientModel | undefined }
    ) {
      state.selectedRecipient = payload;
    },
    addEditMessages(state, { payload }: { payload: MessageModel[] }) {
      let messages = state.messages.slice(),
        recipients = state.recipients.slice();
      const { messageId, sentById, message, sentOn } = payload[0] ?? {};
      const index = messages.findIndex((c) => c.messageId == messageId);
      if (index > -1) {
        messages[index] = payload[0];
      } else {
        messages.push(payload[0]);
      }
      const recipientIndex = recipients.findIndex((c) => c.userId == sentById);

      if (recipientIndex > -1) {
        recipients[recipientIndex] = {
          ...recipients[recipientIndex],
          lastMessage: message,
          lastMessageSentOn: sentOn,
          unReadCount: recipients[recipientIndex].unReadCount + 1,
        };
      }
      return {
        ...state,
        messages,
        recipients,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        chatApi.endpoints.getRecipients.matchFulfilled,
        accountApi.endpoints.getUserRecipients.matchFulfilled
      ),
      (state, { payload }: { payload: RecipientModel[] }) => {
        return {
          ...state,
          recipients: payload,
        };
      }
    );
    builder.addMatcher(chatApi.endpoints.getMessages.matchPending, (state) => {
      return {
        ...state,
        messages: [],
      };
    });
    builder.addMatcher(
      isAnyOf(
        chatApi.endpoints.getMessages.matchFulfilled,
        accountApi.endpoints.getUserMessages.matchFulfilled
      ),
      (state, { payload }: { payload: MessageModel[] }) => {
        return {
          ...state,
          messages: payload,
        };
      }
    );
    builder.addMatcher(
      chatApi.endpoints.addEditMessage.matchFulfilled,
      (state, { payload }) => {
        let messages = state.messages.slice();
        const index = messages.findIndex(
          (c) => c.messageId == payload[0].messageId
        );
        if (index > -1) {
          messages[index] = payload[0];
        } else {
          messages.push(payload[0]);
        }
        return {
          ...state,
          messages,
        };
      }
    );
    builder.addMatcher(
      chatApi.endpoints.getFriendSuggestions.matchFulfilled,
      (state, { payload }: { payload: FriendSuggestionModel[] }) => {
        return {
          ...state,
          friendSuggestionList: payload,
        };
      }
    );
    builder.addMatcher(
      chatApi.endpoints.getFriendRequests.matchFulfilled,
      (state, { payload }: { payload: FriendSuggestionModel[] }) => {
        return {
          ...state,
          friendRequestList: payload,
        };
      }
    );
    builder.addMatcher(
      chatApi.endpoints.sendFriendRequest.matchFulfilled,
      (state, { payload }) => {
        let friendSuggestions = state.friendSuggestionList.slice();
        const index = friendSuggestions.findIndex(
          (c) => c.userId == payload[0].userId
        );
        if (index > -1) {
          friendSuggestions[index] = payload[0];
        } else {
          friendSuggestions.push(payload[0]);
        }
        return {
          ...state,
          friendSuggestionList: friendSuggestions,
        };
      }
    );
    builder.addMatcher(
      chatApi.endpoints.acceptRejectFriendRequest.matchFulfilled,
      (state, { payload }) => {
        let friendRequests = state.friendRequestList.slice();
        const index = friendRequests.findIndex(
          (c) => c.userId == payload[0].userId
        );
        if (index > -1) {
          friendRequests[index] = payload[0];
        } else {
          friendRequests.push(payload[0]);
        }
        return {
          ...state,
          friendRequestList: friendRequests,
        };
      }
    );
  },
});

export const { selectRecipient, addEditMessages } = slice.actions;

export default slice.reducer;

export interface ChatState {
  recipients: RecipientModel[];
  messages: MessageModel[];
  selectedRecipient?: RecipientModel;
  friendSuggestionList: FriendSuggestionModel[];
  friendRequestList: FriendSuggestionModel[];
}
